import type { JSX } from 'react'
/**
 * Application entry.
 *
 * The CRM contributes `crmRoutes` and nothing else — it keeps the host shell,
 * as its README asks. Everything under the shell requires authentication;
 * screens additionally declare the permission that makes them reachable.
 */

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  isRouteErrorResponse,
  useRouteError,
} from 'react-router-dom'

import { AuthProvider } from './auth/auth-context'
import { RequireAuth, RequirePermission } from './auth/route-guard'
import { AppShell } from './shell/app-shell'
import { LoginPage } from './shell/login'
import { OrgUnitsPage, RolesPage, UsersPage } from './shell/admin'
import { crmRoutes } from './crm/routes'

import './styles.css'
import './crm/crm.css'

/**
 * Surfaces loader and action failures instead of a blank screen.
 *
 * A 403 here means the API refused despite the UI allowing it — worth naming
 * explicitly, because it usually points at a decorator and a menu entry that
 * disagree.
 */
function ErrorScreen(): JSX.Element {
  const error = useRouteError()

  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : String(error)

  const forbidden = detail.includes('403') || detail.toLowerCase().includes('forbidden')

  return (
    <div className="erp-centre">
      <h2>{forbidden ? 'Not permitted' : 'Something failed'}</h2>
      <pre className="erp-error">{detail}</pre>
      {forbidden && (
        <p className="erp-muted">
          The server refused this request. You may be missing a permission the
          screen did not check for.
        </p>
      )}
    </div>
  )
}

/** Any CRM permission is enough to enter the CRM subtree. */
const CRM_ANY = [
  'crm.lead.view',
  'crm.deal.view',
  'crm.organization.view',
  'crm.contact.view',
  'crm.task.view',
]

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <ErrorScreen />,
    children: [
      { index: true, element: <Navigate to="/crm/leads" replace /> },

      ...crmRoutes.map((route) => ({
        ...route,
        element: <RequirePermission any={CRM_ANY}>{route.element}</RequirePermission>,
        errorElement: <ErrorScreen />,
      })),

      {
        path: 'admin/users',
        element: (
          <RequirePermission any={['user.view']}>
            <UsersPage />
          </RequirePermission>
        ),
        errorElement: <ErrorScreen />,
      },
      {
        path: 'admin/roles',
        element: (
          <RequirePermission any={['role.view']}>
            <RolesPage />
          </RequirePermission>
        ),
        errorElement: <ErrorScreen />,
      },
      {
        path: 'admin/org-units',
        element: (
          <RequirePermission any={['org_unit.view']}>
            <OrgUnitsPage />
          </RequirePermission>
        ),
        errorElement: <ErrorScreen />,
      },
    ],
  },
])

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  </StrictMode>,
)